import { ChevronLeft, ChevronRight, ExternalLink, X } from "lucide-react";
import { KeyboardEvent as ReactKeyboardEvent, MouseEvent, useEffect, useRef, useState } from "react";
import type { PlaygroundMedia } from "./playgroundLogic";

const focusableSelector = 'a[href], button:not([disabled]), video[controls], [tabindex]:not([tabindex="-1"])';

// LightboxRequest is what result and history thumbnails pass to open the shared viewer.
// 结果区和历史区缩略图打开共享灯箱时传入的参数。
export interface LightboxRequest {
  items: PlaygroundMedia[];
  startIndex: number;
  title: string;
}

// PlaygroundLightbox is a modal media viewer: focus moves in on open, stays trapped, and returns on close.
// 媒体灯箱：打开时聚焦关闭按钮，Tab 循环限制在弹窗内，关闭后焦点回到触发元素。
export function PlaygroundLightbox({ items, startIndex, title, onClose }: { items: PlaygroundMedia[]; startIndex: number; title: string; onClose: () => void }) {
  const [index, setIndex] = useState(() => Math.min(Math.max(startIndex, 0), Math.max(items.length - 1, 0)));
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
      }
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("keydown", closeOnEscape);
      previous?.focus();
    };
  }, []);

  const item = items[index];
  if (!item) return null;
  const multiple = items.length > 1;
  const move = (step: number) => setIndex((current) => (current + step + items.length) % items.length);
  const label = `${item.type === "video" ? "视频" : "图片"} ${index + 1} / ${items.length}`;

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (multiple && event.key === "ArrowLeft") { event.preventDefault(); move(-1); return; }
    if (multiple && event.key === "ArrowRight") { event.preventDefault(); move(1); return; }
    if (event.key !== "Tab" || !dialogRef.current) return;
    const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(focusableSelector));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };
  const closeOnBackdrop = (event: MouseEvent<HTMLDivElement>) => { if (event.target === event.currentTarget) onClose(); };

  return <div className="dialog-backdrop playground-lightbox-backdrop" role="presentation" onClick={closeOnBackdrop}>
    <div ref={dialogRef} className="playground-lightbox" role="dialog" aria-modal="true" aria-labelledby="playground-lightbox-title" onKeyDown={onKeyDown}>
      <div className="dialog-header">
        <div><h2 id="playground-lightbox-title">{title}</h2><p>{label}{multiple ? " · 左右方向键切换" : ""}</p></div>
        <div className="playground-lightbox-actions">
          <a className="secondary-button compact" href={item.url} target="_blank" rel="noreferrer" aria-label={`新窗口打开${label}`}><ExternalLink size={14} />打开原文件</a>
          <button ref={closeRef} type="button" className="icon-button" onClick={onClose} aria-label="关闭预览"><X size={18} /></button>
        </div>
      </div>
      <div className="playground-lightbox-stage">
        {multiple ? <button type="button" className="icon-button playground-lightbox-nav" onClick={() => move(-1)} aria-label="上一个"><ChevronLeft size={22} /></button> : null}
        {item.type === "video"
          ? <video key={item.url} src={item.url} controls preload="metadata" aria-label={label} />
          : <img key={item.url} src={item.url} alt={label} />}
        {multiple ? <button type="button" className="icon-button playground-lightbox-nav" onClick={() => move(1)} aria-label="下一个"><ChevronRight size={22} /></button> : null}
      </div>
    </div>
  </div>;
}
